import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Send as SendIcon, X, Headset, MessageSquare, Phone, Mail, Clock } from "lucide-react";
import { API, api, errMsg } from "../lib/api";

const SUGGESTIONS = [
  "Explain my recent transactions",
  "How do fees work?",
  "How do I withdraw money?",
  "Give me a spending summary",
];

const CHANNELS = [
  { id: "chat", icon: MessageSquare, title: "Chat with an agent", eta: "~3 min" },
  { id: "call", icon: Phone, title: "Get a call back", eta: "~10 min" },
  { id: "email", icon: Mail, title: "Get an email reply", eta: "shortly" },
];

function Countdown({ requestedAt, etaMinutes }) {
  const [left, setLeft] = useState(0);
  useEffect(() => {
    const target = new Date(requestedAt).getTime() + etaMinutes * 60000;
    const tick = () => setLeft(Math.max(0, Math.round((target - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [requestedAt, etaMinutes]);
  if (etaMinutes >= 120) return <span>shortly</span>;
  if (left <= 0) return <span>any moment now</span>;
  const m = Math.floor(left / 60), s = left % 60;
  return <span>in {m}:{String(s).padStart(2, "0")}</span>;
}

export default function Assistant() {
  const nav = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [convId, setConvId] = useState(null);
  const [convStatus, setConvStatus] = useState(null);
  const [showHuman, setShowHuman] = useState(false);
  const [escalating, setEscalating] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!convId) return;
    let stop = false;
    const seen = new Set();
    const tick = async () => {
      try {
        const { data } = await api.get(`/support/conversation/${convId}`);
        setConvStatus(data);
        const fresh = data.messages
          .filter((m) => (m.sender === "agent" || m.sender === "system") && !seen.has(m.msg_id));
        if (fresh.length) {
          fresh.forEach((m) => seen.add(m.msg_id));
          setMessages((cur) => [...cur, ...fresh.map((m) => ({ role: m.sender, content: m.body }))]);
        }
      } catch {}
    };
    tick();
    const id = setInterval(() => !stop && tick(), 5000);
    return () => { stop = true; clearInterval(id); };
  }, [convId]);

  const escalate = async (channel) => {
    if (convId || escalating) return;
    setEscalating(true);
    const lastUser = [...messages].reverse().find((m) => m.role === "user")?.content || "I'd like to talk to a person";
    try {
      const { data } = await api.post("/support/escalate", {
        session_id: sessionId || `app_${Date.now()}`, source: "app", message: lastUser, channel,
      });
      setConvId(data.conv_id); setConvStatus(data); setShowHuman(false);
      toast.success("A Jack Cash agent has been notified");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setEscalating(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/assistant/history");
        setMessages(data.map((m) => ({ role: m.role, content: m.content })));
        if (data[0]) setSessionId(data[0].session_id);
      } catch {}
    })();
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, streaming]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    if (convId) {
      setMessages((m) => [...m, { role: "user", content: msg }]);
      try { await api.post(`/support/conversation/${convId}/message`, { body: msg }); }
      catch (e) { toast.error(errMsg(e)); }
      return;
    }
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      if (!res.ok || !res.body) throw new Error("Assistant unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            }
            if (j.session_id) setSessionId(j.session_id);
            if (j.error) toast.error(j.error);
          } catch {}
        }
      }
    } catch (e) {
      toast.error(errMsg(e));
      setMessages((m) => {
        const copy = [...m];
        if (copy[copy.length - 1]?.role === "assistant" && !copy[copy.length - 1].content) copy.pop();
        return copy;
      });
    } finally { setStreaming(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-background flex justify-center" data-testid="assistant-fullscreen">
     <div className="w-full max-w-md lg:max-w-2xl flex flex-col h-full relative">
      {/* Compact top bar */}
      <header className="flex items-center gap-3 px-4 h-14 border-b border-border glass shrink-0" style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <button data-testid="assistant-close-btn" onClick={() => nav("/app")}
          className="grid place-items-center w-9 h-9 rounded-full bg-secondary active:scale-90 transition-transform">
          <X size={18} />
        </button>
        <div className="flex-1 flex items-center gap-2">
          <span className="grid place-items-center w-8 h-8 rounded-xl text-white" style={{ background: "linear-gradient(135deg,#6B3BF5,#4B23CC)" }}><Sparkles size={16} /></span>
          <span className="font-display font-semibold">Jack Cash Assistant</span>
        </div>
        {!convId && (
          <button data-testid="talk-to-human-btn" onClick={() => setShowHuman(true)}
            className="grid place-items-center w-9 h-9 rounded-full bg-secondary text-primary active:scale-90 transition-transform" aria-label="Talk to a human">
            <Headset size={17} />
          </button>
        )}
      </header>

      {/* Live support status */}
      {convId && convStatus && (
        <div data-testid="support-status-banner" className="mx-4 mt-3 rounded-2xl border border-primary/25 bg-primary/10 text-primary px-4 py-3">
          <div className="flex items-center gap-2 font-semibold text-sm"><Headset size={16} /> You're connected to Jack Cash support</div>
          <div className="text-xs mt-1 flex items-center gap-1.5 text-primary/90">
            <Clock size={13} />
            {convStatus.assigned
              ? "An agent is with you now."
              : <>An agent will reach you by {convStatus.channel === "call" ? "phone" : convStatus.channel} <Countdown requestedAt={convStatus.requested_at} etaMinutes={convStatus.eta_minutes} /></>}
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-secondary p-4 text-sm">
              Hi! I'm your Jack Cash Assistant. I can explain your transactions, fees and activity, and guide you around the app. Need a person? Tap the headset above.
            </div>
            <div className="grid grid-cols-1 gap-2">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="assistant-suggestion" onClick={() => send(s)}
                  className="text-left text-sm px-4 py-3 rounded-2xl bg-card border border-border transition-transform duration-200 active:scale-[0.98]">
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            {m.role === "system" ? (
              <div className="mx-auto text-[11px] text-muted-foreground bg-secondary/60 px-3 py-1.5 rounded-full">{m.content}</div>
            ) : (
              <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground rounded-br-md" : m.role === "agent" ? "bg-success/15 border border-success/30 rounded-bl-md" : "bg-secondary rounded-bl-md"}`}>
                {m.role === "agent" && <div className="text-[10px] font-bold uppercase text-success mb-0.5">Support agent</div>}
                {m.content || (streaming && i === messages.length - 1 ? "…" : "")}
              </div>
            )}
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-4 pt-2 pb-4 border-t border-border bg-background shrink-0" style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}>
        <div className="flex items-center gap-2 bg-secondary rounded-2xl p-1.5">
          <input data-testid="assistant-input" value={input} onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder={convId ? "Message your agent…" : "Ask about your money…"}
            className="flex-1 bg-transparent outline-none px-3 text-sm h-11" />
          <button data-testid="assistant-send-btn" onClick={() => send()} disabled={streaming || !input.trim()}
            className="grid place-items-center w-11 h-11 rounded-xl bg-primary text-primary-foreground disabled:opacity-40 transition-transform duration-200 active:scale-90">
            <SendIcon size={18} />
          </button>
        </div>
      </div>

      {/* Talk-to-a-human sheet */}
      {showHuman && (
        <div className="absolute inset-0 z-10 flex flex-col justify-end" data-testid="human-sheet">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowHuman(false)} />
          <div className="relative bg-background rounded-t-3xl p-5 pb-8 animate-fade-up" style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}>
            <div className="flex items-center justify-between mb-1">
              <h2 className="font-display text-lg font-semibold">Talk to a person</h2>
              <button onClick={() => setShowHuman(false)} className="grid place-items-center w-8 h-8 rounded-full bg-secondary"><X size={16} /></button>
            </div>
            <p className="text-xs text-muted-foreground mb-4">Pick how you'd like a Jack Cash agent to reach you. We'll notify our team right away.</p>
            <div className="space-y-3">
              {CHANNELS.map((c) => (
                <button key={c.id} data-testid={`human-channel-${c.id}`} onClick={() => escalate(c.id)} disabled={escalating}
                  className="w-full flex items-center gap-3 p-4 rounded-2xl border border-border bg-card text-left active:scale-[0.98] transition-transform disabled:opacity-50">
                  <span className="grid place-items-center w-11 h-11 rounded-xl bg-primary/10 text-primary shrink-0"><c.icon size={18} /></span>
                  <span className="flex-1">
                    <span className="font-semibold block">{c.title}</span>
                    <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock size={12} /> {c.eta}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
     </div>
    </div>
  );
}
